import shipsJson from '../../data/ships.json';
import weaponsJson from '../../data/weapons.json';
import enemiesJson from '../../data/enemies.json';
import bossJson from '../../data/boss.json';
import wavesJson from '../../data/waves.json';
import arenaJson from '../../data/arena.json';
import cameraJson from '../../data/camera.json';
import hudJson from '../../data/hud.json';
import rocksJson from '../../data/rocks.json';
import carrierJson from '../../data/carrier.json';
import stationJson from '../../data/station.json';
import missionsJson from '../../data/missions.json';
import artJson from '../../data/art.json';
import equipmentJson from '../../data/equipment.json';
import zonesJson from '../../data/zones.json';
import stationHullJson from '../../data/stationHull.json';
import { schemas, type DataFile, type GameData, type ZoneData } from './schemas';

export * from './schemas';

export const rawData: Record<DataFile, unknown> = {
  ships: shipsJson,
  weapons: weaponsJson,
  enemies: enemiesJson,
  boss: bossJson,
  waves: wavesJson,
  arena: arenaJson,
  camera: cameraJson,
  hud: hudJson,
  rocks: rocksJson,
  carrier: carrierJson,
  station: stationJson,
  missions: missionsJson,
  art: artJson,
  equipment: equipmentJson,
  zones: zonesJson,
  stationHull: stationHullJson,
};

export class DataValidationError extends Error {}

/** Valide et retourne les réglages ; lève une erreur lisible si un fichier est invalide. */
export function parseGameData(raw: Record<DataFile, unknown> = rawData): GameData {
  const out: Partial<GameData> = {};
  const problems: string[] = [];
  for (const key of Object.keys(schemas) as DataFile[]) {
    const result = schemas[key].safeParse(raw[key]);
    if (result.success) {
      (out as Record<string, unknown>)[key] = result.data;
    } else {
      for (const issue of result.error.issues) {
        problems.push(`data/${key}.json → ${issue.path.join('.') || '(racine)'} : ${issue.message}`);
      }
    }
  }
  if (problems.length === 0) problems.push(...crossCheck(out as GameData));
  if (problems.length > 0) throw new DataValidationError(`Réglages invalides :\n${problems.join('\n')}`);
  return out as GameData;
}

/** Cohérence entre fichiers : zones, docks et objectifs des missions. */
function crossCheck(data: GameData): string[] {
  const problems: string[] = [];
  for (const [id, s] of Object.entries(data.missions.scenarios)) {
    const zone = data.zones.zones[s.zone];
    if (!zone) {
      problems.push(`data/missions.json → scenarios.${id}.zone : zone inconnue : ${s.zone}`);
      continue;
    }
    // Une mission d'une autre zone se choisit depuis le porteur (qui saute) ; sinon le dock doit être dans sa zone.
    for (const dock of s.docks) if (dock !== 'carrier' && !zone.docks.includes(dock)) problems.push(`data/missions.json → scenarios.${id}.docks : ${dock} absent de la zone ${s.zone}`);
    if (s.objective === 'turrets' && !zone.fortress) problems.push(`data/missions.json → scenarios.${id}.objective : la zone ${s.zone} n'a pas de forteresse`);
    if (s.objective === 'escort' && !zone.convoy) problems.push(`data/missions.json → scenarios.${id}.objective : la zone ${s.zone} n'a pas de convoi`);
    for (const wave of s.waves) if (!data.waves.waves.some((w) => w.id === wave)) problems.push(`data/missions.json → scenarios.${id}.waves : vague inconnue : ${wave}`);
  }
  return problems;
}

/** Zone demandée, ou la zone de départ si l'identifiant est inconnu. */
export function zoneOf(data: GameData, id: string): { id: string; zone: ZoneData } {
  const zone = data.zones.zones[id];
  return zone ? { id, zone } : { id: data.zones.home, zone: data.zones.zones[data.zones.home]! };
}

/**
 * Réglages vus depuis une zone : son arène remplace celle d'arena.json ; le brouillage réduit la portée du radar
 * et des marqueurs.
 */
export function zoneData(data: GameData, id: string): GameData {
  const { zone } = zoneOf(data, id);
  const scale = zone.radarScale ?? 1;
  const hud = scale === 1 ? data.hud : { ...data.hud, radarRange: data.hud.radarRange * scale, markerRange: data.hud.markerRange * scale };
  return { ...data, arena: zone.arena === 'default' ? data.arena : zone.arena, hud };
}

/**
 * Sauts possibles depuis une zone. Porteur : d'espace à espace, de l'orbite vers sa surface (descente) et retour
 * (remontée). Chasseur (module de saut) : d'espace à espace seulement.
 */
export function jumpTargets(data: GameData, from: string, by: 'carrier' | 'fighter'): string[] {
  const zones = data.zones.zones;
  const here = zones[from];
  if (!here) return [];
  return data.zones.order.filter((id) => {
    const to = zones[id]!;
    if (id === from) return false;
    if (here.kind === 'space' && to.kind === 'space') return true;
    if (by === 'fighter') return false;
    return (to.kind === 'surface' && to.orbitOf === from) || (here.kind === 'surface' && here.orbitOf === id);
  });
}

/** Le passage entre deux zones traverse une atmosphère (descente ou remontée) plutôt qu'un portail. */
export function isAtmosphericJump(data: GameData, from: string, to: string): boolean {
  return data.zones.zones[from]?.kind === 'surface' || data.zones.zones[to]?.kind === 'surface';
}

/** Empreinte FNV-1a des réglages chargés, écrite dans la télémétrie. */
export function dataHash(data: unknown): string {
  const text = JSON.stringify(data);
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, '0');
}

/** Copie profonde modifiable, pour les tests et le panneau de réglage. */
export function cloneData(data: GameData): GameData {
  return structuredClone(data);
}
